import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import {
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
  type DateRangePreset,
  type DateRangeSelection,
} from '../../core/filters/filter-state.model';
import { isValidDateRangeSelection, isValidUtcDay } from '../../core/time/utc-date-range';
import { FaIconComponent, faCalendarDays } from '../../ui/icons/app-icons.registry';

/** The one non-preset choice in the selector. Not a preset token — never in the URL. */
const CUSTOM_CHOICE = 'custom';

/**
 * The date-range control: the seven FR-002 presets plus a custom start/end
 * range.
 *
 * A native `<select>` for the presets is deliberate. The preset set is closed
 * and needs no search (FR-004's search requirement is the *client* selector's),
 * so the combobox machinery of `SearchableSelectComponent` would buy nothing —
 * and a native control is accessible, keyboard-complete, and free of bundle
 * cost on the eager path (R-012). Custom bounds are two `<input type="date">`
 * fields, which are UTC calendar days on the wire and therefore identical for
 * every viewer (SC-002).
 *
 * All resolution — presets to half-open `[fromUtc, toUtc)` windows — belongs to
 * `core/time/utc-date-range.ts` (R-010). This control produces a *selection*
 * and never a window.
 */
@Component({
  selector: 'app-date-range-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
})
export class DateRangePickerComponent {
  readonly selection = input.required<DateRangeSelection>();

  /**
   * Bumped by the bar on Reset and on the FR-019 session clear. Writing the
   * default selection alone cannot close an in-progress Custom draft: the
   * default is a frozen module-level singleton, so re-writing it produces no
   * `selection` input change and the sync effect never runs.
   */
  readonly resetToken = input(0);

  /** Emitted only for a VALID selection: an inverted range never reaches the shared state. */
  readonly selectionChange = output<DateRangeSelection>();

  protected readonly calendarIcon = faCalendarDays;
  protected readonly customChoice = CUSTOM_CHOICE;
  protected readonly presets = DATE_RANGE_PRESETS;
  protected readonly presetLabels = DATE_RANGE_PRESET_LABELS;

  /** Which mode the selector shows. Diverges from `selection()` only while Custom is being filled in. */
  private readonly customChosen = signal(false);
  private readonly startDay = signal('');
  private readonly endDay = signal('');
  private readonly error = signal<string | null>(null);

  protected readonly customOpen = computed(
    () => this.customChosen() || this.selection().kind === CUSTOM_CHOICE,
  );
  protected readonly selectedChoice = computed(() => {
    if (this.customOpen()) return CUSTOM_CHOICE;
    const selection = this.selection();
    return selection.kind === 'preset' ? selection.preset : CUSTOM_CHOICE;
  });
  protected readonly start = this.startDay.asReadonly();
  protected readonly end = this.endDay.asReadonly();
  protected readonly errorMessage = this.error.asReadonly();

  constructor() {
    // Seed the custom fields from an incoming custom selection (a URL restore,
    // or a Reset back to a preset), without publishing anything.
    effect(() => {
      const selection = this.selection();
      untracked(() => {
        if (selection.kind === CUSTOM_CHOICE) {
          this.startDay.set(selection.startUtcDay);
          this.endDay.set(selection.endUtcDay);
        } else {
          this.customChosen.set(false);
          this.error.set(null);
        }
      });
    });

    // An explicit Reset (or session clear) discards the local Custom draft
    // entirely, fields included — the effect above cannot: it only runs when
    // `selection` actually changes. The initial run is skipped so a deep-linked
    // custom selection's seeded fields survive construction.
    let lastResetToken = untracked(() => this.resetToken());
    effect(() => {
      const token = this.resetToken();
      if (token === lastResetToken) return;
      lastResetToken = token;
      untracked(() => {
        this.customChosen.set(false);
        this.startDay.set('');
        this.endDay.set('');
        this.error.set(null);
      });
    });
  }

  protected onChoiceChange(event: Event): void {
    const choice = (event.target as HTMLSelectElement).value;
    if (choice === CUSTOM_CHOICE) {
      // Choosing Custom is not itself a range — nothing is published until Apply.
      this.customChosen.set(true);
      return;
    }

    this.customChosen.set(false);
    this.error.set(null);
    this.selectionChange.emit({ kind: 'preset', preset: choice as DateRangePreset });
  }

  protected onStartInput(event: Event): void {
    this.startDay.set((event.target as HTMLInputElement).value);
  }

  protected onEndInput(event: Event): void {
    this.endDay.set((event.target as HTMLInputElement).value);
  }

  protected applyCustom(): void {
    const startUtcDay = this.startDay();
    const endUtcDay = this.endDay();

    if (!isValidUtcDay(startUtcDay) || !isValidUtcDay(endUtcDay)) {
      this.error.set('Enter both a start and an end date.');
      return;
    }

    const candidate: DateRangeSelection = { kind: CUSTOM_CHOICE, startUtcDay, endUtcDay };
    if (!isValidDateRangeSelection(candidate)) {
      // Edge Cases: inline feedback, the previous valid range stays active, and
      // nothing is published (data-model §10).
      this.error.set('Start date must be on or before the end date.');
      return;
    }

    this.error.set(null);
    this.selectionChange.emit(candidate);
  }
}
